const BigNumber = require('bignumber.js');
const BookingGroup = require('@/lib/GroupBookings/BookingGroup');
const ItemInstance = require('@/libraries/DigiTickets/Orders/ItemInstance');
const Order = require('@/lib/Orders/Order');
const OrderLine = require('@/libraries/DigiTickets/Orders/OrderLine');
const { cloneDeep } = require('@/functions/clone');
const { createFieldGroupCollectionFromOrder } = require('@/lib/CustomFields/fieldGroupCollectionFactory');
const { getDefaultPaymentChannel } = require('@/lib/Channels');
const { shouldGiftAidCart } = require('@/libraries/DigiTickets/GiftAid/giftAid');

/**
 * @constructor
 *
 * @param {CartLineSplitter} cartLineSplitter
 * @param {Hydrator} hydrator
 * @param {PaymentService} paymentService
 * @param {ThirdPartyRefGenerator} thirdPartyRefGenerator
 * @param {UserService} userService
 */
const CartToOrderMapper = function (
    cartLineSplitter,
    hydrator,
    paymentService,
    thirdPartyRefGenerator,
    userService
) {
    /**
     * @type {CartLineSplitter}
     */
    this.cartLineSplitter = cartLineSplitter;

    /**
     * @type {Hydrator}
     */
    this.hydrator = hydrator;

    /**
     * @type {PaymentService}
     */
    this.paymentService = paymentService;

    /**
     * @type {ThirdPartyRefGenerator}
     */
    this.thirdPartyRefGenerator = thirdPartyRefGenerator;

    /**
     * @type {UserService}
     */
    this.userService = userService;
};

CartToOrderMapper.prototype = {
    /**
     * @param {Cart} cart
     */
    map(cart) {
        let lines = cart.itemList.map((line) => line.clone());

        // Split up the lines where discounts are applied
        lines = this.cartLineSplitter.splitDiscounts(lines);

        const order = new Order();
        order.orderBranchID = this.userService.getOrderBranchID();
        order.company = this.userService.getCompany();
        order.customer = cloneDeep(cart.customer);
        order.date = new Date();
        order.orderLevelFieldGroup = cloneDeep(cart.fields);
        order.giftAid = shouldGiftAidCart(cart);
        order.notes = cart.notes;
        order.paidAt = new Date();
        // When printing an order after making a partial payment, we need to include the original booking ref.
        order.bookingRef = cart.originalOrder ? cart.originalOrder.bookingRef : null;
        // TODO: We might not want to do this here anymore because we don't want to add payments until they
        // have been sent to the API. But that would currently break offline orders.
        order.payments = this.paymentService.cleanPayments(cloneDeep(cart.payments));

        const seller = this.userService.currentUser ? this.userService.currentUser.toSeller() : null;
        order.seller = seller;
        order.sellersUserID = seller ? seller.ID : null;

        order.staffRef = cart.staffRef;

        order.thirdPartyRef = this.thirdPartyRefGenerator.generateOrderThirdPartyRef(cart);
        order.thirdPartyID = order.thirdPartyRef;

        order.total = cart.getTotal();

        // Set the payment channel on the order to the default payment channel from ProPoint.
        order.paymentChannel = getDefaultPaymentChannel();

        // Add lines.
        order.items = lines.map((cartLine) => this.mapCartLineToOrderLine(cartLine));

        // Create FieldGroupCollection for backward compatibility.
        order.fields = createFieldGroupCollectionFromOrder(order);

        // Copy any documentNumbers (e.g. an order collection number) if they exist.
        order.documentNumbers = cart.originalOrder ? cloneDeep(cart.originalOrder.documentNumbers) : [];

        order.fulfillmentInstructions = cart.originalOrder
            ? cloneDeep(cart.originalOrder.fulfillmentInstructions)
            : null;

        order.calculate();

        return order;
    },

    /**
     * @param {CartLine} cartLine
     *
     * @return {OrderLine}
     */
    mapCartLineToOrderLine(cartLine) {
        const orderLine = new OrderLine();

        orderLine.baseTotal = cartLine.baseTotal;
        orderLine.branch = cartLine.branch;
        orderLine.branchID = cartLine.branch.ID;
        orderLine.category = cloneDeep(cartLine.item.category);

        // Copy discounts from the CartLine to OrderLine
        if (cartLine.discounts[0]) {
            orderLine.discount = cloneDeep(cartLine.discounts[0]);
            // For variable discounts we need to fetch the value of the discount from the
            // VariableDiscountsCollection on the CartLine and set it as the amount for the discount on the line.
            // Then it will match the structure of other discounts, and also the structure returned for orders
            // from the API with variable discounts.
            if (cartLine.discounts[0].isVariable()) {
                const variableDiscount = cartLine.variableDiscountsCollection?.getVariableDiscount(
                    cartLine.discounts[0].offerID
                );
                if (variableDiscount) {
                    orderLine.discount.amount = new BigNumber(variableDiscount.amount)
                        .multipliedBy(cartLine.quantity)
                        .toNumber();
                    orderLine.discount.qty = cartLine.quantity;
                }
            }
        }

        orderLine.giftAid = cartLine.giftAid;
        orderLine.item = cloneDeep(cartLine.item);
        orderLine.itemID = cartLine.item.ID;
        orderLine.itemType = cartLine.item.itemType;
        orderLine.lineTotal = cartLine.lineTotal;
        orderLine.name = cartLine.getName();
        orderLine.subtitle = cartLine.subtitle;
        orderLine.people = cartLine.item.people;
        orderLine.price = cartLine.price;
        orderLine.priceOfOptions = cartLine.getPriceOfOptions();
        orderLine.bookingFee = cartLine.bookingFee;
        orderLine.bookingFeeSubtotal = cartLine.bookingFeeSubtotal;
        orderLine.qty = cartLine.quantity;
        orderLine.iteminstances = cloneDeep(cartLine.itemInstances);
        orderLine.absQty = Math.abs(cartLine.quantity);
        orderLine.donation = cartLine.donation;
        orderLine.lineNumber = cartLine.lineNumber; // FIXME OrderLine shouldn't have the concept of line numbers.
        orderLine.validityPeriod = cartLine.item.hasOwnProperty('validityPeriod') ? cartLine.item.validityPeriod : null;

        if (cartLine.session) {
            orderLine.event = cloneDeep(cartLine.item.event);
            orderLine.session = cloneDeep(cartLine.session);
        }

        if (cartLine.groupBookingReference) {
            // We don't know the booking group ID, but we can create a new BookingGroup object with the name.
            orderLine.bookingGroup = new BookingGroup({ name: cartLine.groupBookingReference });
        }

        orderLine.thirdPartyRef = cartLine.thirdPartyRef;
        orderLine.parentThirdPartyRef = cartLine.parentThirdPartyRef;

        // Copy OptionSets from CartLine to OrderLine.
        orderLine.optionSet = cartLine.optionSet;
        orderLine.optionSetID = cartLine.optionSet?.ID;

        return orderLine;
    },

    /**
     * Convert the current cart to an order, adding anything in 'additionalData' to the new Order.
     *
     * @param {CartService} cartService
     * @param {object} [apiResponse] The data returned by the API for the order store request. Used to augment
     *      the new Order model with additional properties.
     *
     * @return {Order}
     */
    buildOrderFromApiConfirmation(
        cartService,
        apiResponse
    ) {
        // TODO: Move this toOrder method to here instead of CartService.
        const newOrder = cartService.toOrder();

        // Add bookingRef to the order.
        if (apiResponse && apiResponse.order) {
            if (apiResponse.order.bookingRef) {
                newOrder.bookingRef = apiResponse.order.bookingRef;
            }

            // Add ID to the order.
            if (apiResponse.order.orderID) {
                newOrder.ID = apiResponse.order.orderID;
            }

            // Add documentNumbers to the order.
            if (apiResponse.order.documentNumbers) {
                newOrder.documentNumbers = apiResponse.order.documentNumbers;
            }

            // Add itemInstances to order lines.
            if (apiResponse.order.orderitems) {
                apiResponse.order.orderitems.forEach((partialOrderItem, lineIndex) => {
                    // FIXME: this is going to lose the field data!
                    newOrder.items[lineIndex].iteminstances = this.hydrator.hydrateArray(
                        partialOrderItem.iteminstances,
                        () => new ItemInstance()
                    );

                    if (partialOrderItem.iteminstances.length > 0) {
                        // Add the line IDs to the new order lines.
                        newOrder.items[lineIndex].ID = partialOrderItem.iteminstances[0].ordersItemsID;
                    }
                });

                // Add parentIDs to the new order lines.
                // We can get this by matching parentThirdPartyRef to thirdPartyRef.
                newOrder.items.forEach((line) => {
                    if (!line.parentThirdPartyRef) {
                        return;
                    }
                    const parentLine = newOrder.items.find(
                        (l) => l.thirdPartyRef === line.parentThirdPartyRef
                    );
                    if (parentLine) {
                        line.parentID = parentLine.ID;
                    }
                });
            }
        }

        return newOrder;
    },
};

module.exports = CartToOrderMapper;
